import { FREE_SHIPPING_ANNOUNCEMENT } from '@/lib/shipping';
import { ShoppingBag, Heart, Menu, X, ChevronDown, Gift, Truck, Star, Search, ChevronRight, User, Gem, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCartStore } from '@/stores/cartStore';
import MegaMenuDrawer from './MegaMenuDrawer';
import SearchModal from './SearchModal';
import { ShopifyCartDrawer } from './ShopifyCartDrawer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import grizzleeLogo from '@/assets/grizzlee-gripz-banner-logo.png';
const announcements = [{
  icon: Gift,
  text: 'FREE GIFT with every purchase',
  href: null
}, {
  icon: Truck,
  text: FREE_SHIPPING_ANNOUNCEMENT,
  href: null
}, {
  icon: Star,
  text: 'Join Grizzlee Rewards & Earn',
  href: null
}, {
  icon: Gem,
  text: 'Try Out the First "Grip Selector"',
  href: '/#find-your-color'
}] as const;
const menuItems = [{
  title: 'Shop',
  items: [{
    name: 'Grips',
    href: '/grips',
    subItems: [{
      name: 'Comfort',
      href: '/grips/comfort'
    }, {
      name: 'Dry Feel',
      href: '/grips/dry-feel'
    }, {
      name: 'Gradients',
      href: '/grips/gradient'
    }, {
      name: 'Xtreme Tack',
      href: '/grips/xtreme-tacky'
    }, {
      name: 'Whitwell Collab',
      href: '/collaborations/lee-whitwell'
    }, {
      name: 'All Grips',
      href: '/grips'
    }]
  }, {
    name: 'Tacky Grip Spray',
    href: '/grip-pro'
  }, {
    name: 'Apparel',
    href: '/apparel',
    subItems: [{
      name: 'T-Shirts',
      href: '/apparel/tshirts'
    }, {
      name: 'Hoodies & Crewnecks',
      href: '/apparel/sweatshirts'
    }, {
      name: 'Hats',
      href: '/apparel/hats'
    }, {
      name: 'All Apparel',
      href: '/apparel'
    }]
  }, {
    name: 'Accessories',
    href: '/accessories',
    subItems: [{
      name: 'Towels',
      href: '/golf-towels'
    }, {
      name: 'Gear',
      href: '/accessories?category=accessories'
    }, {
      name: 'All Accessories',
      href: '/accessories'
    }]
  }]
}, {
  title: 'Grizzlee Gripz',
  items: [{
    name: 'About Us',
    href: '/about'
  }, {
    name: 'GripX Technology',
    href: '/gripx-technology'
  }, {
    name: 'Blogs',
    href: '/blog'
  }, {
    name: 'Join Us',
    href: '/ambassador',
    subItems: [{
      name: 'Ambassador Program',
      href: '/ambassador'
    }, {
      name: 'Sponsorship',
      href: '/ambassador#apply'
    }, {
      name: 'Affiliate Program',
      href: '/ambassador'
    }, {
      name: 'Grizzlee Rewards',
      href: '/auth/register'
    }]
  }]
}, {
  title: 'Support',
  items: [{
    name: 'Contact Us',
    href: '/contact'
  }, {
    name: 'Wholesale / B2B',
    href: '/wholesale'
  }]
}];
const Header = () => {
  const {
    openCart
  } = useCart();
  const {
    totalItems: wishlistCount,
    openWishlist
  } = useWishlist();
  const shopifyCartItems = useCartStore((state) => state.getTotalItems());
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const CurrentIcon = announcements[currentAnnouncement].icon;
  return <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement Bar - using CSS animation to avoid forced reflows */}
      <div className="text-secondary py-2 px-4 overflow-hidden bg-primary">
        <div className="container mx-auto flex items-center justify-center lg:justify-between text-xs sm:text-sm font-medium">
          {/* Spacer for symmetry - desktop only */}
          <div className="hidden lg:block w-[100px]" />

          {/* Announcements - Center */}
          <div key={currentAnnouncement} className="flex items-center justify-center animate-fade-in gap-[4px] text-primary-foreground">
            {announcements[currentAnnouncement].href ?
          <Link to={announcements[currentAnnouncement].href!} className="flex items-center gap-2 hover:underline">
                <CurrentIcon className="w-4 h-4" />
                <span>{announcements[currentAnnouncement].text}</span>
                <CurrentIcon className="w-4 h-4" />
              </Link> :
          <>
                <CurrentIcon className="w-4 h-4" />
                <span>{announcements[currentAnnouncement].text}</span>
                <CurrentIcon className="w-4 h-4" />
              </>
          }
          </div>

          {/* Login Links - Right (hidden on mobile, shown in mobile menu) */}
          <div className="hidden lg:flex items-center gap-2 text-sm font-semibold">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/auth/login"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground hover:text-primary transition-colors"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Team
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p>Team / Player Login</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/wholesale/dashboard"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground hover:text-primary transition-colors"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Business
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom"><p>Wholesale Partner Portal</p></TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      
      {/* Main Header */}
      <div className="bg-background/95 backdrop-blur-md border-b border-border">
        <div className="px-2 sm:px-3">
          {/* Mobile & Tablet Header Layout */}
          <div className="flex lg:hidden items-center justify-between h-14">
            {/* Left - Hamburger + Logo */}
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)]" aria-label="Menu">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <a href="/" className="flex items-center">
                <img src={grizzleeLogo} alt="Grizzlee Gripz - Premium Pickleball Overgrips and Accessories" width={200} height={21} sizes="(max-width: 768px) 140px, 200px" className="h-5 sm:h-6 md:h-8 w-auto max-w-[130px] sm:max-w-[160px] md:max-w-[200px] object-contain" />
              </a>
            </div>

            {/* Right - Icons */}
            <div className="flex items-center gap-1.5">
              <button onClick={openWishlist} className="relative flex items-center justify-center w-9 h-9 rounded-full transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)]" aria-label="Wishlist">
                <Heart className="w-5 h-5" />
                {wishlistCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center animate-bounce-subtle">
                    {wishlistCount}
                  </span>}
              </button>
              <ShopifyCartDrawer />
            </div>
          </div>

        </div>
        <div className="container mx-auto px-0">
          {/* Desktop Header Layout */}
          <div className="hidden lg:flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img src={grizzleeLogo} alt="Grizzlee Gripz - Premium Pickleball Overgrips and Accessories" width={320} height={33} sizes="(max-width: 1280px) 260px, 320px" className="h-10 xl:h-12 w-auto max-w-[260px] xl:max-w-[320px] object-contain transition-transform duration-200 hover:scale-105" />
            </a>

            {/* Desktop Navigation */}
            <nav className="flex items-center space-x-6 xl:space-x-8">
              {menuItems.map((menu) => <div key={menu.title} className="relative" onMouseEnter={() => setActiveMenu(menu.title)} onMouseLeave={() => setActiveMenu(null)}>
                  <button className="flex items-center space-x-1 py-6 font-medium group text-sm xl:text-base text-black">
                    <span className="relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-black after:origin-bottom-right after:transition-transform after:duration-300 group-hover:after:scale-x-100 group-hover:after:origin-bottom-left">
                      {menu.title}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-black transition-transform duration-200 ${activeMenu === menu.title ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <MegaMenuDrawer isOpen={activeMenu === menu.title} items={menu.items} />
                </div>)}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center space-x-3 xl:space-x-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => setSearchOpen(true)} className="relative p-2 rounded-full transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)] hover:shadow-[0_0_15px_rgba(251,146,60,0.4)] text-black" aria-label="Search products">
                    <Search className="w-5 h-5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Search Products</p></TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/auth/login" className="relative p-2 rounded-full transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)] hover:shadow-[0_0_15px_rgba(251,146,60,0.4)] text-black" aria-label="Customer login">
                    <User className="w-5 h-5" aria-hidden="true" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Customer Login</p></TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={openWishlist} className="relative p-2 rounded-full transition-all hover:ring-2 hover:ring-primary shadow-[0_4px_15px_rgba(0,0,0,0.15)] hover:shadow-[0_0_15px_rgba(251,146,60,0.4)] text-black" aria-label="View wishlist">
                    <Heart className="w-5 h-5" aria-hidden="true" />
                    {wishlistCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center animate-bounce-subtle">
                        {wishlistCount}
                      </span>}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>Wishlist</p></TooltipContent>
              </Tooltip>
              <ShopifyCartDrawer />
            </div>
          </div>
        </div>

      {/* Mobile & Tablet Menu */}
      {mobileMenuOpen && <div className="lg:hidden bg-background border-t border-border animate-slide-down max-h-[70vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-2">
            <Accordion type="single" collapsible className="w-full">
              {menuItems.map((menu, index) => <AccordionItem key={menu.title} value={`item-${index}`} className="border-b border-border">
                  <AccordionTrigger className="py-4 font-display font-semibold text-base hover:no-underline hover:text-primary">
                    {menu.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-2 pb-2 space-y-1">
                      {menu.items.map((item) => <div key={item.name}>
                          <Link to={item.href} className="flex items-center justify-between py-2.5 px-3 rounded-md text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                            <span>{item.name}</span>
                            <ChevronRight className="w-4 h-4 opacity-50" />
                          </Link>
                          {'subItems' in item && item.subItems && <div className="pl-4 space-y-1 mt-1">
                              {item.subItems.map((subItem) => <Link key={subItem.name} to={subItem.href} className="flex items-center justify-between py-2 px-3 rounded-md text-sm text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                  <span>{subItem.name}</span>
                                  <ChevronRight className="w-3 h-3 opacity-50" />
                                </Link>)}
                            </div>}
                        </div>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {/* Search Bar in Mobile Menu */}
            <button onClick={() => {
            setSearchOpen(true);
            setMobileMenuOpen(false);
          }} className="w-full flex items-center gap-3 py-3 px-4 mt-2 bg-muted/50 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors">
              <Search className="w-5 h-5" />
              <span className="font-medium">Search products...</span>
            </button>

            {/* Team & Business Login Links */}
            <div className="flex items-center gap-2 py-3 px-4 mt-2">
              <Link
                to="/auth/login"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-foreground/60 text-foreground text-sm font-semibold hover:bg-foreground hover:text-background transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                Team
              </Link>
              <Link
                to="/wholesale/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-foreground/60 text-foreground text-sm font-semibold hover:bg-foreground hover:text-background transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                Business
              </Link>
            </div>
          </nav>
        </div>}
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>;
};
export default Header;